import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import {
  GET_SINGLE_PLAYLIST,
  UPDATE_SINGLE_PLAYLIST,
  DELETE_SINGLE_PLAYLIST,
  GET_TREATMENT_HISTORY,
} from "./queries";
import {
  FormInputs,
  PlaylistsModalProps,
  SinglePlaylistResponse,
  DeleteInputs,
} from "./types";
import { Playlist, VoiceMessage } from "types/api";
import {
  convertStringToDate,
  secondsToHMS,
  transformDateUTC,
} from "utils/date-utils";
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid,
  Stack,
  TextField,
  Snackbar,
  Alert,
  Collapse,
  CircularProgress,
  MenuItem,
  Select as MuiSelect,
  // FormControlLabel,
  // Checkbox,
} from "@mui/material";
import { style } from "./styles";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { DateTimePickerComponent } from "../DateTimePicker";
import { ActivitiesFilteringTable } from "../ActivitiesFilteringTable";
import { useNavigate } from "react-router-dom";
import { VoiceMessageList } from "components/molecules/VoiceMessageList";
import { isAfter, isBefore } from "date-fns";
import { ConfirmDialog } from "components/molecules/ConfirmDialog";
import { dialogContent } from "../dialogContent";
import { StatusChip } from "../StatusChip";
import moment from "moment";
import { ButtonContainer } from "components/molecules/Modal/styles";
import { Loader } from "pages/UserDetails/styles";
import { Error } from "../CreatePlaylistModal/styles";

export const SinglePlaylistModal = (props: PlaylistsModalProps) => {
  const [editView, setEditView] = useState<boolean>(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showError, setShowError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [regexError, setRegexError] = useState<boolean>(false);
  const [areRowsSelected, setAreRowsSelected] = useState<boolean>(false);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState<boolean>(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const [playlistActivities, setPlaylistActivities] = useState<VoiceMessage[]>(
    []
  );
  const [playlistExistError, setPlaylistExistError] = useState<boolean>(false);
  const [totalLength, setTotalLength] = useState<number>(0);
  const [defaultLength, setDefaultLength] = useState<number>(0);

  const [addedActivities, setAddedActivities] = useState<number>(0);

  const {
    data: singlePlaylistData,
    error: singlePlaylistError,
    loading: singlePlaylistLoading,
  } = useQuery<SinglePlaylistResponse>(GET_SINGLE_PLAYLIST, {
    variables: { id: props.playlistId },
    fetchPolicy: "network-only",
    skip: !props.open,
  });

  const { data: programsData } = useQuery(GET_TREATMENT_HISTORY, {
    variables: { accountId: props.accountId },
    fetchPolicy: "cache-and-network",
    skip: !props.accountId,
  });

  useEffect(() => {
    setIsLoading(singlePlaylistLoading);
  }, [singlePlaylistLoading]);

  const [updatePlaylist, { error: updateError }] = useMutation<FormInputs>(
    UPDATE_SINGLE_PLAYLIST
  );

  const [deletePlaylist, { error: deleteError }] = useMutation<DeleteInputs>(
    DELETE_SINGLE_PLAYLIST,
    {
      variables: { id: props.playlistId },
    }
  );
  useEffect(() => {
    setAddedActivities(0);
  }, []);

  useEffect(() => {
    if (singlePlaylistError || updateError || deleteError) {
      setShowError(true);
    }
  }, [singlePlaylistError, updateError, deleteError]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isValid },
    setValue,
  } = useForm<FormInputs>({
    mode: "onChange",
  });

  const playlist: Playlist | undefined = singlePlaylistData?.playlist;
  const [titleName, setTitleName] = useState<string | null>(
    playlist?.title ? playlist?.title : null
  );

  let playlistDurationInSeconds: any = playlistActivities
    ?.map(
      (singleVoiceMessage) => singleVoiceMessage?.audioFile?.durationInSeconds
    )
    ?.reduce(
      (previousValue?, currentValue?) =>
        (previousValue ?? 0) + (currentValue ?? 0),
      0
    );
  // setTotalLength(playlistDurationInSeconds);

  useEffect(() => {
    playlistDurationInSeconds = playlistActivities
      ?.map(
        (singleVoiceMessage) => singleVoiceMessage?.audioFile?.durationInSeconds
      )
      ?.reduce(
        (previousValue?, currentValue?) =>
          (previousValue ?? 0) + (currentValue ?? 0),
        0
      );

    playlistActivities.length === 0
      ? setTotalLength(
          playlistDurationInSeconds + addedActivities + defaultLength
        )
      : setTotalLength(playlistDurationInSeconds + addedActivities);
  }, [addedActivities, playlistActivities, defaultLength]);

  const voiceMessages =
    playlist?.playlistVoiceMessages
      ?.map((pvm) => pvm?.voiceMessage)
      ?.flatMap((x) => (!!x ? [x] : [])) ?? [];

  useEffect(() => {
    if (singlePlaylistData) {
      setValue("title", playlist?.title);
      setValue("startOn", playlist?.startOn ?? null);
      setValue("endOn", playlist?.endOn ?? null);
      setValue("isFreeTrial", playlist?.isFreeTrial ?? false);
      setValue("isTracked", playlist?.isTracked ?? true);
      setValue("programId", playlist?.program?.id ?? null);
    }
  }, [singlePlaylistData]);

  const [idsForPlaylist, setIdsForPlaylist] = useState<number[]>([]);
  const [startDateValue, setStartDateValue] = useState<Date | null>(
    playlist?.startOn ?? null
  );
  const [endDateValue, setEndDateValue] = useState<Date | null>(
    playlist?.endOn ?? null
  );
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);

  useEffect(() => {
    const formattedStart = convertStringToDate(startDateValue?.toString());
    const formattedEnd = convertStringToDate(endDateValue?.toString());
    if (
      !!formattedStart &&
      !!formattedEnd &&
      isAfter(formattedStart as Date, formattedEnd as Date)
    ) {
      setStartDateError("Start date cannot be after end date");
    } else {
      setStartDateError(null);
    }
    if (!moment(startDateValue).isValid()) {
      setStartDateError("Invalid start date");
    }
  }, [startDateValue, endDateValue]);

  const handleChangeStart = (newValue: Date | null) => {
    setStartDateValue(newValue);
    setValue("startOn", newValue);
  };

  useEffect(() => {
    const formattedStart = convertStringToDate(startDateValue?.toString());
    const formattedEnd = convertStringToDate(endDateValue?.toString());
    if (
      !!formattedStart &&
      !!formattedEnd &&
      isBefore(formattedEnd as Date, formattedStart as Date)
    ) {
      setEndDateError("End date cannot be before start date");
    } else {
      setEndDateError(null);
    }
    if (!moment(endDateValue).isValid()) {
      setEndDateError("Invalid end date");
    }
  }, [endDateValue, startDateValue]);

  const handleChangeEnd = (newValue: Date | null) => {
    setEndDateValue(newValue);
    setValue("endOn", newValue);
  };

  // set date state values with editView dependency, so react-hook-form values are replaced with the db values upon X'ing out of the edit playlist modal
  useEffect(() => {
    if (playlist?.startOn) {
      const startDate = new Date(playlist.startOn);
      setStartDateValue(startDate);
      setValue("startOn", startDate);
      setStartDateError(null);
    }
  }, [playlist?.startOn, editView]);

  useEffect(() => {
    if (playlist?.endOn) {
      const endDate = new Date(playlist.endOn);
      setEndDateValue(endDate);
      setValue("endOn", endDate);
      setEndDateError(null);
    }
  }, [playlist?.endOn, editView]);

  useEffect(() => {
    if (props.playlistId && voiceMessages) {
      setPlaylistActivities(voiceMessages.map((voiceMessage) => voiceMessage));
    }
  }, [props.playlistId]);

  useEffect(() => {
    if (
      voiceMessages &&
      voiceMessages.length > 0 &&
      playlistActivities.length === 0
    ) {
      setPlaylistActivities(voiceMessages.map((voiceMessage) => voiceMessage));
    }
  }, [voiceMessages?.length]);

  useEffect(() => {
    setIdsForPlaylist(playlistActivities.map((activity) => activity.id));
  }, [playlistActivities]);

  const checkIfSelectedRows = () => {
    if (areRowsSelected) {
      setConfirmSubmitOpen(true);
    }
  };

  //filter PVMs by those that are not associated with the current playlist, as this will be addressed in ActivitiesFilteringTable dynamically
  const filteredPVMs = props.allPVMs?.filter(
    (pvm) => pvm?.playlist?.id !== props.playlistId
  );

  const onSubmit: SubmitHandler<FormInputs> = async (response) => {
    const startDateOnly = startDateValue?.toISOString()?.split("T")[0];
    const startDateTime = startDateOnly?.concat("T00:00:00.000Z");

    const endDateOnly = endDateValue?.toISOString()?.split("T")[0];
    const endDateTime = endDateOnly?.concat("T23:59:59.999Z");

    if (
      idsForPlaylist.length > 0 &&
      isValid &&
      !startDateError &&
      !endDateError
    ) {
      try {
        const regex = new RegExp(/^week\s*[1-9][0-9]*$/);

        const titleIsValid = regex.test(response.title?.toLowerCase()!);
        if (titleIsValid) {
          const result = await updatePlaylist({
            variables: {
              id: props.playlistId,
              title: response.title?.toLowerCase(),
              voiceMessageIds: idsForPlaylist,
              startOn: startDateTime,
              endOn: endDateTime,
              isFreeTrial: response.isFreeTrial,
              isTracked: response.isTracked,
              programId: response.programId,
            },
          });

          // Only navigate if update was successful
          if (result.data) {
            setEditView(false);
            navigate("/refresh");
          }
        } else {
          setRegexError(true);
        }
      } catch (e: any) {
        console.error("Failed to update playlist:", e);

        // Extract specific error message if available
        let message = "Failed to update playlist. ";
        if (e.graphQLErrors && e.graphQLErrors.length > 0) {
          message = e.graphQLErrors[0].message;
        } else if (e.message) {
          message += e.message;
        } else {
          message += "Please check your input and try again.";
        }

        setErrorMessage(message);
        setShowError(true);
      }
    }
  };

  const ButtonLogic = () => {
    const isDisabled =
      !isValid ||
      idsForPlaylist.length === 0 ||
      !!startDateError ||
      !!endDateError;
    const variant = isDisabled ? "outlined" : "contained";
    return (
      <>
        <Collapse in={idsForPlaylist.length < 1}>
          <Alert severity="error">Please choose at least one activity.</Alert>
        </Collapse>
        <Button
          className="button"
          type="submit"
          variant={variant}
          disabled={isDisabled}
          onClick={() => checkIfSelectedRows()}
        >
          Submit
        </Button>
      </>
    );
  };

  const getDatainSeconds = (rows: any) => {
    let selectedActivitiesDuration = 0;
    rows.forEach((selectedRows: any) => {
      selectedActivitiesDuration +=
        selectedRows?.voiceMessage?.audioFile?.durationInSeconds;
    });
    setAddedActivities(selectedActivitiesDuration);
  };

  const isPlaylistPresent = (e: any, title?: any) => {
    setTitleName(e.target.value.toLowerCase());
    const regex = new RegExp(/^week\s*[1-9][0-9]*$/);
    const isValid = regex.test(e.target.value.trim().toLowerCase());

    if (
      props?.playlists?.includes(e.target.value.trim()) &&
      e.target.value !== title
    ) {
      setPlaylistExistError(true);
    } else {
      setPlaylistExistError(false);
    }
    if (!isValid) {
      setRegexError(true);
    } else {
      setRegexError(false);
    }
  };

  return (
    <>
      <Modal
        open={props.open}
        onClose={() => {
          setEditView(false);
          setAddedActivities(0);
          props.onClose();
          setPlaylistExistError(false);
          setTitleName(playlist?.title!);
          reset();
        }}
      >
        {isLoading ? (
          <Loader>
            <CircularProgress />
          </Loader>
        ) : (
          <Box sx={style}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid
                container
                justifyContent="space-between"
                sx={{ paddingBottom: "0.5rem" }}
              >
                <Grid item>
                  <Typography variant="h4">
                    {editView ? "Edit Playlist" : "Playlist Detail"}
                  </Typography>
                </Grid>
                <Grid item>
                  <ButtonContainer>
                    {!editView && (
                      <Button
                        className="button"
                        onClick={() => {
                          props.setDuplicatePlaylist?.(playlist);
                          props.onClose();
                          props.openDuplicate?.();
                        }}
                      >
                        Duplicate Playlist
                      </Button>
                    )}

                    <Button
                      className="button"
                      onClick={() => {
                        setConfirmDeleteOpen(true);
                      }}
                    >
                      Delete Playlist
                    </Button>
                    {editView ? (
                      <Button
                        className="button"
                        variant="outlined"
                        onClick={() => {
                          // Don't reset activities - preserve the edited state
                          setEditView(false);
                          reset();
                        }}
                      >
                        Go To Playlist Detail
                      </Button>
                    ) : (
                      <Button
                        className="button"
                        variant="outlined"
                        onClick={() => {
                          setEditView(true);
                        }}
                      >
                        Edit Playlist
                      </Button>
                    )}
                    <Button
                      className="button"
                      variant="outlined"
                      onClick={() => {
                        setEditView(false);
                        setAddedActivities(0);
                        setPlaylistExistError(false);
                        setPlaylistActivities([]); // Clear activities on close
                        props.onClose();
                        reset();
                        setTitleName(playlist?.title!);
                      }}
                    >
                      X
                    </Button>
                  </ButtonContainer>
                </Grid>
              </Grid>

              <br />
              <Grid
                container
                justifyContent="space-between"
                sx={{ padding: "0.5rem 0 0.5rem 0" }}
                spacing={1}
              >
                <Grid item xs={4}>
                  <Stack direction="column">
                    <Typography variant="h6">
                      {!editView ? "Playlist Title" : "Playlist Title*"}
                    </Typography>
                    {!editView ? (
                      <Typography>{playlist?.title}</Typography>
                    ) : (
                      <TextField
                        defaultValue={playlist?.title}
                        required={true}
                        value={titleName?.toLowerCase()}
                        {...register("title", {
                          onChange: (e) => {
                            isPlaylistPresent(e, playlist?.title);
                            setTitleName(e.target.value?.toLowerCase());
                          },
                        })}
                      />
                    )}
                    {editView && playlistExistError ? (
                      <Error>A playlist already exists with that name*</Error>
                    ) : null}

                    {editView && regexError ? (
                      <Error>
                        Playlist name should be in 'week X' format as X should
                        be a number only
                      </Error>
                    ) : null}
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack direction="column">
                    <Typography variant="h6"> Total Length</Typography>
                    <Typography>{secondsToHMS(totalLength)}</Typography>
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack direction="column">
                    <Typography variant="h6"> Status</Typography>
                    <Typography component={"span"}>
                      <StatusChip isActive={playlist?.isActive} />
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Stack direction="column">
                    <Typography variant="h6">
                      {!editView ? "Start Date" : "Start Date*"}
                    </Typography>
                    {!editView ? (
                      <Typography>
                        {transformDateUTC(playlist?.startOn?.toString())}
                      </Typography>
                    ) : (
                      <Controller
                        name="startOn"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { ref, ...rest } }) => (
                          <DateTimePickerComponent
                            dateValue={startDateValue}
                            handleChange={handleChangeStart}
                            label={"Start Date"}
                            required={true}
                            textFieldProps={{
                              error: !!startDateError,
                              helperText: startDateError,
                            }}
                            {...rest}
                          />
                        )}
                      />
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack direction="column">
                    <Typography variant="h6">
                      {!editView ? "End Date" : "End Date*"}
                    </Typography>
                    {!editView ? (
                      <Typography>
                        {transformDateUTC(playlist?.endOn?.toString())}
                      </Typography>
                    ) : (
                      <Controller
                        name="endOn"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { ref, ...rest } }) => (
                          <DateTimePickerComponent
                            dateValue={endDateValue}
                            handleChange={handleChangeEnd}
                            label={"End Date"}
                            required={true}
                            textFieldProps={{
                              error: !!endDateError,
                              helperText: endDateError,
                            }}
                            {...rest}
                          />
                        )}
                      />
                    )}
                  </Stack>
                </Grid>
              </Grid>
              <Grid>
                {!editView ? (
                  <>
                    <Grid>
                      <Typography variant="h6">Playlist</Typography>
                    </Grid>
                    <Grid>
                      <VoiceMessageList
                        voiceMessages={playlistActivities}
                        showIcon={false}
                        isDragDisabled={true}
                      />
                    </Grid>
                  </>
                ) : (
                  <Grid item>
                    <Typography component={"span"}>
                      {editView && (
                        <ActivitiesFilteringTable
                          setDefaultLength={setDefaultLength}
                          setAreRowsSelected={setAreRowsSelected}
                          editMode={true}
                          playlistActivities={playlistActivities}
                          setPlaylistActivities={setPlaylistActivities}
                          filteredPVMs={filteredPVMs}
                          getDatainSeconds={getDatainSeconds}
                          th={props?.therapist}
                        />
                      )}
                    </Typography>
                  </Grid>
                )}

                <Grid container>
                  <ConfirmDialog
                    confirmOpen={confirmSubmitOpen}
                    dialogContent={dialogContent}
                    onSubmit={() => handleSubmit(onSubmit)()}
                    onClose={() => setConfirmSubmitOpen(false)}
                  />
                  <ConfirmDialog
                    confirmOpen={confirmDeleteOpen}
                    dialogContent={
                      <>
                        <Typography>
                          Are you sure you want to delete this playlist?
                        </Typography>
                        <Typography>{playlist?.title}</Typography>
                      </>
                    }
                    onSubmit={async () => (
                      await deletePlaylist(),
                      props.onClose(),
                      navigate("/refresh")
                    )}
                    onClose={() => setConfirmDeleteOpen(false)}
                  />
                  <Grid item>{editView && <ButtonLogic />}</Grid>
                </Grid>
              </Grid>
            </form>
          </Box>
        )}
      </Modal>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: '100%' }}>
          {updateError && (updateError.message || "Failed to update playlist. Please try again.")}
          {!idsForPlaylist?.length && "No activities selected. Please select at least one activity."}
          {deleteError && (deleteError.message || "Failed to delete playlist. Please try again.")}
          {singlePlaylistError && (singlePlaylistError.message || "Failed to fetch playlist data. Please refresh the page.")}
        </Alert>
      </Snackbar>
    </>
  );
};
